import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { sheetToAoaFullRange } from '../../tax/vat/readXlsx';
import { transformRows, verify, OutputRow } from './transform';
import { buildSundumbiBlob } from './buildExcel';

const OUTPUT_HEADERS = ['날짜', '이름', '품목', '수량'];

function todayStr(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export default function SundumbiSettlement() {
  const [rows, setRows] = useState<OutputRow[]>([]);
  const [issues, setIssues] = useState<string[]>([]);
  const [fileName, setFileName] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const handleFile = (file: File) => {
    setBusy(true);
    setError('');
    setRows([]);
    setIssues([]);
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target!.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array' });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = sheetToAoaFullRange(ws);
        const out = transformRows(aoa);
        if (out.length === 0) {
          setError('변환할 데이터가 없습니다. 취합 시트(이름·품목 컬럼)를 확인해 주세요.');
        } else {
          setRows(out);
          setIssues(verify(aoa, out).issues);
        }
      } catch (err: any) {
        setError(`파일을 읽지 못했습니다: ${err?.message || String(err)}`);
      } finally {
        setBusy(false);
      }
    };
    reader.onerror = () => { setError('파일 읽기 오류'); setBusy(false); };
    reader.readAsArrayBuffer(file);
  };

  const download = async () => {
    const blob = await buildSundumbiBlob(rows);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `순수한둠비_정산_${todayStr()}.xlsx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const itemCount = rows.length;
  const personCount = rows.filter(r => r.name !== '').length;
  const homonymCount = rows.filter(r => r.homonym).length;

  return (
    <div>
      <h1 className="text-2xl font-light text-gray-800 mb-6">순수한둠비 월말정산</h1>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <span className="px-4 py-2 bg-jeju-brown text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity">
              {busy ? '처리 중...' : '취합 주문서 엑셀 올리기'}
            </span>
            {fileName && <span className="text-xs text-gray-500">{fileName}</span>}
            <input
              type="file"
              accept=".xlsx,.xls"
              className="hidden"
              onChange={(e) => { if (e.target.files && e.target.files[0]) handleFile(e.target.files[0]); e.currentTarget.value = ''; }}
            />
          </label>
          {rows.length > 0 && (
            <div className="flex items-center gap-3">
              <span className="text-sm text-gray-500">
                <strong className="text-gray-800">{personCount}</strong>명 · 품목 <strong className="text-gray-800">{itemCount}</strong>행
              </span>
              <button
                onClick={download}
                className="px-4 py-2 border border-jeju-brown text-jeju-brown rounded-lg text-sm font-medium hover:bg-jeju-brown hover:text-white transition-colors"
              >결과 엑셀 다운로드</button>
            </div>
          )}
        </div>

        {error && <div className="px-6 py-4 text-sm text-red-500">{error}</div>}

        {rows.length > 0 && (
          <div className={`px-6 py-2 text-xs ${issues.length ? 'text-red-600 bg-red-50' : 'text-green-700 bg-green-50'}`}>
            {issues.length ? `검증 경고 — ${issues.join(' · ')}` : '검증 통과 — 원본 품목 수와 출력 행수 일치'}
          </div>
        )}

        {homonymCount > 0 && (
          <div className="px-6 py-2 text-xs text-gray-600 bg-white border-b border-gray-100 flex items-center gap-2">
            <span className="inline-block w-3 h-3 rounded-sm" style={{ backgroundColor: '#FEF08A' }} />
            동명이인(이름은 같지만 전화·주소가 다른 사람) <strong className="text-gray-800">{homonymCount}</strong>명 — 이름 셀을 노란색으로 표시했어요.
          </div>
        )}

        {rows.length > 0 && (
          <div className="overflow-auto max-h-[calc(100vh-300px)]">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0 z-10">
                <tr>
                  {OUTPUT_HEADERS.map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-200">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map((r, i) => (
                  <tr key={i} className={`hover:bg-gray-50 transition-colors ${r.name ? 'border-t border-gray-200' : ''}`}>
                    <td className="px-4 py-2 text-gray-600 whitespace-nowrap font-mono text-xs">{r.date}</td>
                    <td className="px-4 py-2 text-gray-800 font-medium whitespace-nowrap" style={r.homonym ? { backgroundColor: '#FEF08A' } : undefined}>{r.name}</td>
                    <td className="px-4 py-2 text-gray-700">{r.item}</td>
                    <td className="px-4 py-2 text-gray-800">{r.qty}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {rows.length === 0 && !error && !busy && (
          <div className="px-6 py-12 text-center text-sm text-gray-400">
            월별로 취합한 주문서 엑셀을 올리면 날짜·이름·품목·수량으로 정리됩니다.
          </div>
        )}
      </div>
    </div>
  );
}
